// PPCUsers.js — PPC User Management (rebuilt clean)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PPC_API_URL } from './PPCContext';

const ROLES = ['admin','incharge','supervisor','factory_head','factory_manager','department_head','managing_director','viewer'];

const ROLE_COLORS = {
  admin:            { bg:'#fef2f2', color:'#d32f2f' },
  incharge:         { bg:'#eff6ff', color:'#1565c0' },
  supervisor:       { bg:'#f0fdf4', color:'#15803d' },
  factory_head:     { bg:'#fff7ed', color:'#c2410c' },
  factory_manager:  { bg:'#faf5ff', color:'#7e22ce' },
  department_head:  { bg:'#ecfdf5', color:'#059669' },
  managing_director:{ bg:'#fffbeb', color:'#b45309' },
  viewer:           { bg:'#f8fafc', color:'#64748b' },
};

const AVATAR_COLORS = ['#d32f2f','#1565c0','#059669','#d97706','#7b1fa2','#0097a7','#e64a19','#388e3c'];
const avatarBg = (i) => AVATAR_COLORS[i % AVATAR_COLORS.length];
const initials = (n) => (n||'?').split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase();
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const emptyForm = () => ({
  username:'', full_name:'', email:'', password:'', role:'viewer',
  can_edit:false, can_delete_entries:false, can_import:false,
  can_export:true, can_approve:false, is_active:true,
});

// ─────────────────────────────────────────────────────────────────────────────
export default function PPCUsers() {
  const navigate = useNavigate();

  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [toast,      setToast]      = useState(null);

  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(emptyForm());
  const [errors,     setErrors]     = useState({});
  const [saving,     setSaving]     = useState(false);
  const [showPwd,    setShowPwd]    = useState(false);

  const [delUser,    setDelUser]    = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  const toast$ = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${PPC_API_URL}/users.php?action=list`, auth());
      if (res.data?.success) setUsers(res.data.users || []);
    } catch (e) {
      toast$('Failed to load users', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Filter ───────────────────────────────────────────────────────────────
  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const mQ = !q || u.username.toLowerCase().includes(q) || (u.full_name||'').toLowerCase().includes(q) || (u.email||'').toLowerCase().includes(q);
    const mR = !roleFilter || u.role === roleFilter;
    return mQ && mR;
  });

  const summary = {
    total:  users.length,
    active: users.filter(u=>u.is_active).length,
    admins: users.filter(u=>u.role==='admin').length,
  };

  // ── Form helpers ─────────────────────────────────────────────────────────
  const setF = (k, v) => { setForm(p=>({...p,[k]:v})); setErrors(p=>({...p,[k]:''})); };

  const openAdd = () => { setEditId(null); setForm(emptyForm()); setErrors({}); setShowPwd(false); setShowForm(true); };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({
      username:           u.username||'',
      full_name:          u.full_name||'',
      email:              u.email||'',
      password:           '',
      role:               u.role||'viewer',
      can_edit:           !!u.can_edit,
      can_delete_entries: !!u.can_delete_entries,
      can_import:         !!u.can_import,
      can_export:         !!u.can_export,
      can_approve:        !!u.can_approve,
      is_active:          !!u.is_active,
    });
    setErrors({});
    setShowPwd(false);
    setShowForm(true);
  };

  const validate = () => {
    const e = {};
    if (!form.username.trim()) e.username = 'Username is required';
    if (!form.full_name.trim()) e.full_name = 'Full name is required';
    if (!editId && !form.password.trim()) e.password = 'Password is required for new user';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (users.find(u => u.username.toLowerCase()===form.username.toLowerCase() && u.id!==editId))
      e.username = 'Username already taken';
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = { ...form };
    if (editId) { payload.id = editId; if (!payload.password) delete payload.password; }
    try {
      let res;
      if (editId) {
        res = await axios.put(`${PPC_API_URL}/users.php?action=update`, payload, auth());
      } else {
        res = await axios.post(`${PPC_API_URL}/users.php?action=create`, payload, auth());
      }
      if (res.data?.success) {
        toast$(editId ? 'User updated!' : 'User created!');
        setShowForm(false);
        fetchUsers();
      } else {
        toast$(res.data?.error || 'Save failed', 'error');
      }
    } catch (e) {
      toast$(e.response?.data?.error || 'Save failed', 'error');
    }
    setSaving(false);
  };

  const handleToggle = async (u) => {
    try {
      const res = await axios.put(
        `${PPC_API_URL}/users.php?action=toggle-status`, { id: u.id }, auth()
      );
      if (res.data?.success) { toast$(res.data.message||'Status updated'); fetchUsers(); }
      else toast$(res.data?.error||'Failed', 'error');
    } catch (e) { toast$('Failed to update status', 'error'); }
  };

  const handleDelete = async () => {
    if (!delUser) return;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${PPC_API_URL}/users.php?action=delete&id=${delUser.id}`, auth()
      );
      if (res.data?.success) {
        toast$('User deleted');
        setDelUser(null);
        fetchUsers();
      } else toast$(res.data?.error||'Delete failed', 'error');
    } catch (e) { toast$(e.response?.data?.error||'Delete failed', 'error'); }
    setDeleting(false);
  };

  const resetPwd = async (u) => {
    const pwd = prompt(`Set new password for ${u.username}:`);
    if (!pwd) return;
    try {
      const res = await axios.put(
        `${PPC_API_URL}/users.php?action=reset-password`, { id: u.id, password: pwd }, auth()
      );
      if (res.data?.success) toast$('Password reset successfully');
      else toast$(res.data?.error||'Failed', 'error');
    } catch (e) { toast$('Failed to reset password', 'error'); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Header */}
      <header style={S.hdr}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button style={S.hbtn} onClick={() => navigate('/ppc')}>← Back</button>
          <span style={S.htitle}>👥 User Management</span>
        </div>
        <button style={S.addBtn} onClick={openAdd}>＋ Add User</button>
      </header>

      {/* Stats */}
      <div style={S.statRow}>
        {[
          { label:'Total Users', value:summary.total,  color:'#1565c0' },
          { label:'Active',      value:summary.active, color:'#059669' },
          { label:'Admins',      value:summary.admins, color:'#d32f2f' },
        ].map((s,i) => (
          <div key={i} style={S.statCard}>
            <div style={{ fontSize:28, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:11, color:'#6b7280', marginTop:3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <input placeholder="Search username, name, email…"
          value={search} onChange={e=>setSearch(e.target.value)} style={S.searchInp} />
        <select value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} style={S.sel}>
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g,' ').toUpperCase()}</option>)}
        </select>
        <span style={{ fontSize:11, color:'#9ca3af', marginLeft:'auto' }}>
          {filtered.length} user{filtered.length!==1?'s':''}
        </span>
      </div>

      {/* Table */}
      <div style={S.card}>
        {loading ? (
          <div style={S.center}>Loading users…</div>
        ) : filtered.length===0 ? (
          <div style={S.center}>No users found</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={S.tbl}>
              <thead>
                <tr>
                  {['#','User','Email','Role','Permissions','Status','Actions'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u,idx) => {
                  const rc = ROLE_COLORS[u.role] || { bg:'#f8fafc', color:'#64748b' };
                  const perms = [
                    u.can_edit           && 'Edit',
                    u.can_delete_entries && 'Delete',
                    u.can_import         && 'Import',
                    u.can_export         && 'Export',
                    u.can_approve        && 'Approve',
                  ].filter(Boolean);
                  return (
                    <tr key={u.id} style={S.tr}
                      onMouseEnter={e=>e.currentTarget.style.background='#f5f7ff'}
                      onMouseLeave={e=>e.currentTarget.style.background=''}>
                      <td style={{...S.td, color:'#9ca3af', width:36}}>{idx+1}</td>
                      <td style={S.td}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:34, height:34, borderRadius:'50%', background:avatarBg(idx), display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff', flexShrink:0 }}>
                            {initials(u.full_name||u.username)}
                          </div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:12 }}>{u.full_name||u.username}</div>
                            <div style={{ fontSize:10, color:'#9ca3af', fontFamily:'monospace' }}>{u.username}</div>
                          </div>
                        </div>
                      </td>
                      <td style={S.td}><span style={{ fontSize:11 }}>{u.email||'—'}</span></td>
                      <td style={S.td}>
                        {u.role
                          ? <span style={{ padding:'3px 9px', borderRadius:12, fontSize:10, fontWeight:700, ...rc }}>{u.role.replace(/_/g,' ').toUpperCase()}</span>
                          : <span style={{ fontSize:11, color:'#9ca3af' }}>—</span>
                        }
                      </td>
                      <td style={S.td}>
                        <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                          {perms.length===0
                            ? <span style={{ fontSize:10, color:'#9ca3af' }}>None</span>
                            : perms.map(p => (
                              <span key={p} style={{ padding:'2px 7px', borderRadius:10, fontSize:10, fontWeight:600, background:'#e3f2fd', color:'#1565c0' }}>{p}</span>
                            ))}
                        </div>
                      </td>
                      <td style={{...S.td, textAlign:'center'}}>
                        <button onClick={()=>handleToggle(u)}
                          style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600, color:u.is_active?'#059669':'#9ca3af', background:'transparent', border:'none', cursor:'pointer' }}>
                          <span style={{ width:7, height:7, borderRadius:'50%', background:u.is_active?'#059669':'#9ca3af', display:'inline-block' }}/>
                          {u.is_active?'Active':'Inactive'}
                        </button>
                      </td>
                      <td style={{...S.td, textAlign:'center'}}>
                        <div style={{ display:'flex', gap:4, justifyContent:'center', flexWrap:'wrap' }}>
                          <button onClick={()=>openEdit(u)} style={S.actBtn('#1565c0')}>EDIT</button>
                          <button onClick={()=>resetPwd(u)} style={S.actBtn('#d97706')}>PWD</button>
                          <button onClick={()=>setDelUser(u)} style={S.actBtn('#d32f2f')}>DEL</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── ADD / EDIT MODAL ── */}
      {showForm && (
        <div style={S.overlay} onClick={()=>setShowForm(false)}>
          <div style={S.modal} onClick={e=>e.stopPropagation()}>
            <div style={S.mHdr}>
              <div>
                <div style={{ color:'#fff', fontSize:15, fontWeight:700 }}>
                  {editId ? '✏️ Edit User' : '➕ Add User'}
                </div>
                <div style={{ color:'rgba(255,255,255,.7)', fontSize:11, marginTop:2 }}>
                  {editId ? form.username : 'Fill in user details'}
                </div>
              </div>
              <button onClick={()=>setShowForm(false)} style={S.xBtn}>×</button>
            </div>

            <div style={{ padding:'20px', display:'grid', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Username *" error={errors.username}>
                  <input value={form.username} onChange={e=>setF('username',e.target.value)}
                    placeholder="e.g. john.doe" style={inp(errors.username)} disabled={!!editId} />
                </Field>
                <Field label="Full Name *" error={errors.full_name}>
                  <input value={form.full_name} onChange={e=>setF('full_name',e.target.value)}
                    placeholder="Full name" style={inp(errors.full_name)} />
                </Field>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Email" error={errors.email}>
                  <input type="email" value={form.email} onChange={e=>setF('email',e.target.value)}
                    placeholder="email@choicegroup.in" style={inp(errors.email)} />
                </Field>
                <Field label={editId ? 'New Password (leave blank to keep)' : 'Password *'} error={errors.password}>
                  <div style={{ position:'relative' }}>
                    <input type={showPwd?'text':'password'} value={form.password}
                      onChange={e=>setF('password',e.target.value)}
                      placeholder={editId?'Leave blank to keep current':'Set password'}
                      style={{ ...inp(errors.password), paddingRight:40 }} />
                    <button type="button" onClick={()=>setShowPwd(p=>!p)}
                      style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#6b7280' }}>
                      {showPwd?'🙈':'👁'}
                    </button>
                  </div>
                </Field>
              </div>

              <Field label="Role">
                <select value={form.role} onChange={e=>setF('role',e.target.value)} style={{ ...inp(), cursor:'pointer' }}>
                  {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g,' ').toUpperCase()}</option>)}
                </select>
              </Field>

              <div>
                <div style={S.secDiv}>Permissions</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginTop:8 }}>
                  {[
                    { key:'can_edit',           label:'Can Edit' },
                    { key:'can_delete_entries', label:'Can Delete' },
                    { key:'can_import',         label:'Can Import' },
                    { key:'can_export',         label:'Can Export' },
                    { key:'can_approve',        label:'Can Approve' },
                  ].map(p => (
                    <label key={p.key} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', border:`1.5px solid ${form[p.key]?'#1565c0':'#e2e6ea'}`, borderRadius:8, cursor:'pointer', background:form[p.key]?'#dbeafe':'#fff', transition:'all .15s' }}>
                      <input type="checkbox" checked={form[p.key]} onChange={e=>setF(p.key,e.target.checked)}
                        style={{ width:15, height:15, accentColor:'#1565c0', cursor:'pointer' }} />
                      <span style={{ fontSize:12, fontWeight:600, color:form[p.key]?'#1565c0':'#374151' }}>{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', background:'#f8f9fa', borderRadius:8, border:'1px solid #e2e6ea' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>Account Active</div>
                  <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>
                    {form.is_active ? 'User can log in' : 'User login disabled'}
                  </div>
                </div>
                <Toggle checked={form.is_active} onChange={v=>setF('is_active',v)} />
              </div>
            </div>

            <div style={{ padding:'0 20px 20px', display:'flex', gap:10 }}>
              <button onClick={()=>setShowForm(false)} style={S.cancelBtn}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={S.saveBtn}>
                {saving ? 'Saving…' : editId ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {delUser && (
        <div style={S.overlay} onClick={()=>setDelUser(null)}>
          <div style={{ background:'#fff', borderRadius:16, padding:'30px 24px', width:'100%', maxWidth:380, textAlign:'center' }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:40, marginBottom:12 }}>⚠️</div>
            <div style={{ fontSize:16, fontWeight:700, color:'#d32f2f', marginBottom:8 }}>Delete User?</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:18 }}>
              Permanently delete <strong>{delUser.username}</strong>?
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setDelUser(null)} style={S.cancelBtn}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ ...S.saveBtn, background:'linear-gradient(135deg,#dc2626,#ef4444)' }}>
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position:'fixed', bottom:22, right:22, padding:'11px 20px', borderRadius:9,
          background:toast.type==='error'?'#d32f2f':'#059669',
          color:'#fff', fontSize:13, fontWeight:500, zIndex:9999, boxShadow:'0 4px 18px rgba(0,0,0,.25)' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <label style={{ display:'block', fontSize:10, fontWeight:700, color:'#6b7280', marginBottom:5, textTransform:'uppercase', letterSpacing:'.4px' }}>{label}</label>
    {children}
    {error && <span style={{ fontSize:10, color:'#d32f2f', marginTop:3, display:'block' }}>{error}</span>}
  </div>
);

const Toggle = ({ checked, onChange }) => (
  <label style={{ position:'relative', width:44, height:24, flexShrink:0, cursor:'pointer' }}>
    <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)}
      style={{ opacity:0, width:0, height:0, position:'absolute' }} />
    <span style={{ position:'absolute', inset:0, borderRadius:24, transition:'background .2s', background:checked?'#059669':'#d1d5db', cursor:'pointer' }}>
      <span style={{ position:'absolute', width:18, height:18, borderRadius:'50%', background:'#fff', top:3, left:checked?23:3, transition:'left .2s', boxShadow:'0 1px 4px rgba(0,0,0,.2)' }}/>
    </span>
  </label>
);

const inp = (err) => ({ width:'100%', padding:'9px 11px', borderRadius:7, border:`1.5px solid ${err?'#d32f2f':'#e2e6ea'}`, fontFamily:'inherit', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' });
const S = {
  page:     { minHeight:'100vh', background:'#f0f2f5', fontFamily:"'DM Sans',sans-serif", fontSize:13 },
  hdr:      { background:'linear-gradient(135deg,#d32f2f,#1565c0)', padding:'0 24px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, boxShadow:'0 2px 12px rgba(211,47,47,.3)' },
  hbtn:     { background:'rgba(255,255,255,.18)', border:'1px solid rgba(255,255,255,.3)', color:'#fff', padding:'6px 14px', borderRadius:20, cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:500 },
  htitle:   { color:'#fff', fontSize:16, fontWeight:700 },
  addBtn:   { background:'#059669', border:'none', color:'#fff', padding:'8px 18px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit' },
  statRow:  { display:'flex', gap:14, padding:'20px 24px 0' },
  statCard: { background:'#fff', borderRadius:12, padding:'16px 22px', border:'1px solid #e2e6ea', textAlign:'center', minWidth:110 },
  toolbar:  { display:'flex', gap:10, padding:'14px 24px', alignItems:'center', flexWrap:'wrap' },
  searchInp:{ flex:1, maxWidth:320, padding:'8px 12px', border:'1px solid #e2e6ea', borderRadius:8, fontFamily:'inherit', fontSize:12, outline:'none', background:'#fff' },
  sel:      { padding:'8px 12px', border:'1px solid #e2e6ea', borderRadius:8, fontFamily:'inherit', fontSize:12, background:'#fff', cursor:'pointer', minWidth:130 },
  card:     { margin:'0 24px 30px', background:'#fff', borderRadius:14, border:'1px solid #e2e6ea', overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,.06)' },
  center:   { padding:'48px', textAlign:'center', color:'#9ca3af', fontSize:13 },
  tbl:      { width:'100%', borderCollapse:'collapse', fontSize:12 },
  th:       { padding:'10px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:11, color:'#6b7280', textAlign:'left', whiteSpace:'nowrap' },
  tr:       { borderBottom:'1px solid #f3f4f6', transition:'background .1s' },
  td:       { padding:'10px 12px', verticalAlign:'middle' },
  actBtn:   (bg) => ({ padding:'4px 9px', border:'none', borderRadius:5, background:bg, color:'#fff', fontSize:10, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }),
  overlay:  { position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
  modal:    { background:'#fff', borderRadius:16, width:'100%', maxWidth:600, maxHeight:'93vh', overflowY:'auto' },
  mHdr:     { background:'linear-gradient(135deg,#d32f2f,#1565c0)', padding:'15px 20px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', borderRadius:'16px 16px 0 0', position:'sticky', top:0, zIndex:10 },
  xBtn:     { width:28, height:28, borderRadius:'50%', border:'none', background:'rgba(255,255,255,.2)', color:'#fff', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 },
  secDiv:   { fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.5px', borderBottom:'1px solid #e2e6ea', paddingBottom:6, marginBottom:4 },
  cancelBtn:{ flex:1, padding:11, borderRadius:8, border:'1.5px solid #e2e6ea', background:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer' },
  saveBtn:  { flex:2, padding:11, borderRadius:8, border:'none', background:'linear-gradient(135deg,#d32f2f,#1565c0)', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer' },
};
